import { useState, useEffect } from "react";
import Loader from "components/Loader";
import MobileSupportTabBar from "components/TabBars/MobileSupportTabBar";
import HelpCard from "components/Cards/HelpCard";
import { Button, Col, Form } from "react-bootstrap";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";
import { getInputFieldStyle, getButtonStyle, getTextareaStyle } from "utils/formStyles";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import ToastAlert from "components/Alert/ToastAlert";

export default function ContentCreationPage() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultGot, setIsResultGot] = useState(false);
  const [inputText, setInputText] = useState("");
  const [showSearchItem, setShowSearchItem] = useState("");
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [wordCount, setWordCount] = useState(0);

  const [errors, setErrors] = useState({ inputText: "" });
  const [tabs, setTabs] = useState([]);
  const firstLevelTopic = "SOCIAL";
  const secondLevelTopic = "META + INSTA";
  const thirdLevelTopic = "Content Creation";

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
    setInsufficientCreditsData(null);
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputText(text);
    
    // Count words
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    let error = "";
    if (name === "inputText") {
      if (!inputText.trim()) {
        error = "Please enter some content";
      } else if (wordCount > 500) {
        error = "Content must be less than 500 words";
      } else if (wordCount < 10) {
        error = "Content must be at least 10 words";
      }
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleReset = () => {
    setShowSearchItem("");
    setIsResultGot(false);
    setInputText("");
    setWordCount(0);
    setShowErrorNotification(false);
    setErrorMessage("");
    setShowSuccessNotification(false);
    setSuccessMessage("");
    setErrors({ inputText: "" });
  };

  // Function to handle copying with success notification
  const handleCopyToClipboard = async (text, message = "Content copied to clipboard!") => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccessMessage(message);
      setShowSuccessNotification(true);
    } catch (err) {
      console.error('Failed to copy: ', err);
      setErrorMessage("Failed to copy to clipboard");
      setShowErrorNotification(true);
    }
  };

  // Mock content generation function
  const generateMockContent = (input) => {
    const templates = [
      {
        title: "Engaging Social Media Post",
        content: `🌟 Transform your social media presence with this amazing content! 

${input}

✨ Here's what makes this special:
• Authentic storytelling that connects
• Visual appeal that stops the scroll
• Call-to-action that drives engagement
• Hashtags that boost discoverability

Ready to take your content to the next level? Let's make it happen! 

#ContentCreation #SocialMedia #Engagement #DigitalMarketing #ContentStrategy #SocialMediaTips #CreativeContent #BrandBuilding #OnlinePresence #ContentMarketing`
      },
      {
        title: "Instagram Story Series",
        content: `📱 STORY SERIES IDEA:

Based on your content: "${input.substring(0, 100)}..."

🎯 Story 1: Hook your audience
"Did you know that..."

🎯 Story 2: Share the main insight
"Here's the secret..."

🎯 Story 3: Provide value
"Try this technique..."

🎯 Story 4: Call to action
"Save this post and share with someone who needs to see this!"

💡 Pro tip: Use interactive stickers like polls, questions, and sliders to boost engagement!

#InstagramStories #ContentPlanning #SocialMediaStrategy #EngagementBoost #StoryTelling #DigitalContent #SocialMediaTips #ContentCreator #BrandStory #InstagramMarketing`
      },
      {
        title: "Facebook Post with High Engagement",
        content: `🚀 FACEBOOK POST THAT CONVERTS:

${input}

🔥 Why this works:
→ Starts with an attention-grabbing hook
→ Provides genuine value to your audience  
→ Includes a clear call-to-action
→ Uses emojis strategically for visual appeal
→ Encourages comments and shares

💬 ENGAGEMENT BOOSTER:
Ask your audience: "What's your experience with this? Share in the comments below!"

👥 Tag 3 friends who would find this valuable!

#FacebookMarketing #SocialMediaContent #EngagementStrategy #ContentMarketing #SocialMediaTips #DigitalMarketing #CommunityBuilding #SocialMediaGrowth #ContentStrategy #OnlineMarketing`
      }
    ];

    return templates;
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setShowSearchItem(inputText.substring(0, 50) + (inputText.length > 50 ? "..." : ""));
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2500));
      
      // Generate mock content
      const content = generateMockContent(inputText);
      
      setTabs([
        {
          label: "Social Media Post",
          element: (
            <div className="p-3">
              <div className="mb-3">
                <h5>{content[0].title}</h5>
                <p className="text-muted">Optimized for maximum engagement across platforms</p>
              </div>
              <div className="content-output bg-light p-3 rounded">
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                  {content[0].content}
                </pre>
              </div>
              <div className="mt-3">
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => handleCopyToClipboard(content[0].content, "Social media post copied to clipboard!")}
                >
                  Copy Content
                </Button>
                <small className="text-muted ms-3">
                  Word count: {content[0].content.split(/\s+/).length} words
                </small>
              </div>
            </div>
          ),
        },
        {
          label: "Instagram Stories",
          element: (
            <div className="p-3">
              <div className="mb-3">
                <h5>{content[1].title}</h5>
                <p className="text-muted">Multi-part story sequence for Instagram</p>
              </div>
              <div className="content-output bg-light p-3 rounded">
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                  {content[1].content}
                </pre>
              </div>
              <div className="mt-3">
                <Button 
                  variant="outline-success" 
                  size="sm"
                  onClick={() => handleCopyToClipboard(content[1].content, "Instagram story series copied to clipboard!")}
                >
                  Copy Story Series
                </Button>
                <small className="text-muted ms-3">
                  Word count: {content[1].content.split(/\s+/).length} words
                </small>
              </div>
            </div>
          ),
        },
        {
          label: "Facebook Post",
          element: (
            <div className="p-3">
              <div className="mb-3">
                <h5>{content[2].title}</h5>
                <p className="text-muted">Facebook-optimized content with engagement tactics</p>
              </div>
              <div className="content-output bg-light p-3 rounded">
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                  {content[2].content}
                </pre>
              </div>
              <div className="mt-3">
                <Button 
                  variant="outline-warning" 
                  size="sm"
                  onClick={() => handleCopyToClipboard(content[2].content, "Facebook post copied to clipboard!")}
                >
                  Copy Facebook Post
                </Button>
                <small className="text-muted ms-3">
                  Word count: {content[2].content.split(/\s+/).length} words
                </small>
              </div>
            </div>
          ),
        },
      ]);
      setIsResultGot(true);
    } catch (error) {
      console.error("Submission error:", error);
      setErrorMessage(error.message || "Failed to generate content. Please try again.");
      setShowErrorNotification(true);
      setIsResultGot(false);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    let isValid = wordCount >= 10 && wordCount <= 500;
    setIsFormValid(isValid);
  }, [inputText, wordCount]);

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      {/* Toast Notifications */}
      <ToastAlert
        show={showErrorNotification}
        message={errorMessage}
        type="error"
        icon="fas fa-exclamation-circle"
        duration={5000}
        onClose={() => setShowErrorNotification(false)}
        position="top-right"
      />
      <ToastAlert
        show={showSuccessNotification}
        message={successMessage}
        type="success"
        icon="fas fa-check-circle"
        duration={3000}
        onClose={() => setShowSuccessNotification(false)}
        position="top-right"
      />
      <h1 className="d-flex fw-normal justify-content-start align-items-center fw-bold">
        {thirdLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / {secondLevelTopic} / <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
        Transform your ideas into engaging <b>social media content</b>. 
        Enter your content (up to 500 words) and get optimized posts for 
        Instagram and Facebook that drive engagement and reach.
      </p>
      <div className="row">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <HelpCard
            topic={"How To Use"}
            description={`"Content Creation" transforms your raw ideas into polished, engaging social media posts optimized for Instagram and Facebook, complete with hashtags and engagement strategies.`}
            image={"/icon/edit-two.svg"}
          />
        </div>
      </div>
      <div className="p-2 mb-2 rounded">
        <div className="d-flex fw-bold mb-0">
          Create Your Content
        </div>
        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              Enter your content or ideas (10-500 words) to generate optimized social media posts.
            </p>
            <Form.Group>
              <div className="d-flex mt-2">
                <Col sm={6} className="me-2">
                  <Form.Control
                    as="textarea"
                    rows={8}
                    name="inputText"
                    placeholder="Enter your content, ideas, or message that you want to transform into engaging social media posts. You can include product descriptions, blog excerpts, announcements, or any content you want to optimize for social media..."
                    value={inputText}
                    onChange={handleInputChange}
                    isInvalid={!!errors.inputText}
                    onBlur={handleBlur}
                    required
                    className="px-2"
                    style={getTextareaStyle('type6')}
                  />
                  <div className="d-flex justify-content-between mt-1">
                    <small className={`text-muted ${wordCount > 500 ? 'text-danger' : ''}`}>
                      Word count: {wordCount}/500
                    </small>
                    {errors.inputText && (
                      <small className="text-danger">{errors.inputText}</small>
                    )}
                  </div>
                </Col>
                <div className="col-lg-4 col-md-4 col-sm-12 mb-2 me-1">
                  <div className="d-flex justify-content-start align-items-end">
                    <Button
                      variant="dark"
                      className="me-1"
                      onClick={handleSubmit}
                      disabled={!isFormValid}
                      style={getButtonStyle('type6', 'primary')}
                    >
                      Create Content
                    </Button>
                    <Button
                      variant="outline-dark"
                      className="me-1"
                      onClick={handleReset}
                      style={getButtonStyle('type6', 'outline')}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="p-2">
        {showSearchItem !== "" && (
          <div>
            <p className="d-flex fw-bold mb-0">
              Generated content for
              <span className="search-item">&nbsp;"{showSearchItem}"</span>
            </p>
          </div>
        )}
        {isLoading && <Loader />}
        {isResultGot && <MobileSupportTabBar tabs={tabs} />}
      </div>
      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        isOpen={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        currentBalance={insufficientCreditsData?.currentBalance}
        requiredCredits={insufficientCreditsData?.requiredCredits}
        shortfall={insufficientCreditsData?.shortfall}
        actionName={insufficientCreditsData?.actionName}
      />
    </div>
  );
}
